import { useEffect, useRef, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext.jsx';
import { api } from '../api.js';
import { toast } from '../components/Toasts.jsx';
import RoleCoverage from '../components/RoleCoverage.jsx';
import ChipInput from '../components/ChipInput.jsx';
import AiSuggestPanel from '../components/AiSuggestPanel.jsx';
import SkillGapPanel from '../components/SkillGapPanel.jsx';
import { FormSkeleton } from '../components/Skeleton.jsx';
import { inputCls } from '../components/ui';
import { formatClock, formatDate } from '../util/time.js';
import { normalizePrefs, prefsPayload } from '../util/prefs.js';

const EMPTY = {
  summary: '',
  targetNarrative: '',
  seniority: '',
  skills: [],
  experience: [],
  education: [],
  certifications: [],
  languages: [],
};

const EMPTY_PREFS = {
  targetRoles: [],
  targetLocations: [],
  // T096 — multi-valued (operator can target ['mid', 'senior'] etc.)
  seniority: [],
  remotePreference: 'any',
  salaryMinimum: '',
  salaryCurrency: '',
  excludedCompanies: [],
  digestHour: 8,
};

// T163 — stable fingerprints of the editable state. Compared against a
// "last saved" snapshot to know when the form is dirty (career profile is the
// operator's most valuable data and was silently discarded on navigation).
const profileFingerprint = (p) =>
  JSON.stringify({
    summary: p.summary ?? '',
    targetNarrative: p.targetNarrative ?? '',
    seniority: p.seniority ?? '',
    skills: p.skills ?? [],
    experience: p.experience ?? [],
    education: p.education ?? [],
    certifications: p.certifications ?? [],
    languages: p.languages ?? [],
  });
const prefsFingerprint = (p) =>
  JSON.stringify(normalizePrefs(p));

// T267(2) — re-attach a chip's metadata by NAME, not by position.
//
// ChipInput hands back a plain list of strings, and both callers below used to
// read the old metadata at the SAME INDEX. Deleting a chip shifts everything
// after it up one, so removing "English" (native) from [English, Danish] left
// Danish holding English's "native" — silently claiming a language level the
// operator never entered, on the document that drives his scoring.
//
// Matching on the name is order-independent, so a reorder or a delete carries
// each entry's own metadata with it. Only genuinely new names get the default.
export function remapByName(names, previous, defaults) {
  const byName = new Map((previous ?? []).map((e) => [String(e?.name ?? '').toLowerCase(), e]));
  return names.map((name) => {
    const prior = byName.get(String(name).toLowerCase());
    return prior ? { ...prior, name } : { name, ...defaults };
  });
}

export default function ProfilePage() {
  const { user, refresh: refreshAuth } = useAuth();
  const [profile, setProfile] = useState(EMPTY);
  const [meta, setMeta] = useState({ hasCvSnapshot: false, hasLinkedinSnapshot: false, embeddingsReady: false });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState(null);
  const [error, setError] = useState(null);

  // Hunt preferences (moved here from SettingsPage — these belong with the
  // job hunter profile, not with the app-level Account settings).
  const [prefs, setPrefs] = useState(EMPTY_PREFS);
  const [prefsBusy, setPrefsBusy] = useState(false);
  const [prefsSavedAt, setPrefsSavedAt] = useState(null);

  // Import (T008 CV/LinkedIn-PDF via Claude, T009 LinkedIn-export ZIP via local CSV)
  const [importOpen, setImportOpen] = useState(false);
  const [importCandidates, setImportCandidates] = useState({ cv: [], linkedinPdf: [], linkedinExport: [] });
  const [importBusyId, setImportBusyId] = useState(null);
  const [importError, setImportError] = useState(null);
  const [importDraft, setImportDraft] = useState(null); // {draft, changes, source}
  const importPanelRef = useRef(null);

  // T163 — fingerprints of the last-saved profile + prefs. Current state is
  // compared against these to derive `dirty`; updated on load + every save.
  const savedProfileRef = useRef(profileFingerprint(EMPTY));
  const savedPrefsRef = useRef(prefsFingerprint(EMPTY_PREFS));

  // When a fresh draft arrives, scroll the diff panel into view so the
  // operator can't miss the two-step (parse → apply) handoff.
  useEffect(() => {
    if (importDraft && importPanelRef.current) {
      importPanelRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [importDraft]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const body = await api.get('/profile');
      const p = body.profile ?? EMPTY;
      const next = {
        summary: p.summary ?? '',
        targetNarrative: p.targetNarrative ?? '',
        seniority: p.seniority ?? '',
        skills: p.skills ?? [],
        experience: p.experience ?? [],
        education: p.education ?? [],
        certifications: p.certifications ?? [],
        languages: p.languages ?? [],
      };
      setProfile(next);
      savedProfileRef.current = profileFingerprint(next); // T163 — fresh baseline
      setMeta({
        hasCvSnapshot: !!p.hasCvSnapshot,
        hasLinkedinSnapshot: !!p.hasLinkedinSnapshot,
        embeddingsReady: !!p.embeddingsReady,
      });
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  // Sync prefs from the auth user (already loaded by AuthContext via /me).
  // Runs on load AND after savePrefs → refreshAuth, so it doubles as the
  // "saved" baseline for T163 dirty-tracking.
  useEffect(() => {
    const next = normalizePrefs(user?.preferences ?? {});
    setPrefs(next);
    savedPrefsRef.current = prefsFingerprint(next); // T163 — fresh baseline
  }, [user]);

  // T163 — unsaved-edit guard. `dirty` recomputes on every profile/prefs edit;
  // when set, a beforeunload handler warns before a hard navigation (reload, tab
  // close, typing a new URL). NOTE: in-app SPA navigation (sidebar link, in-app
  // back) is NOT intercepted — react-router v7's useBlocker requires a data
  // router (createBrowserRouter) and this app mounts <BrowserRouter>; that
  // migration is tracked separately. This still closes the most common vector.
  const dirty =
    profileFingerprint(profile) !== savedProfileRef.current ||
    prefsFingerprint(prefs) !== savedPrefsRef.current;

  // T267(4) — `dirty` existed only to arm beforeunload, which cannot fire on
  // an in-app sidebar click (react-router v7 needs a data router for that, a
  // migration out of proportion to this). So the most common way to lose edits
  // was also the silent one. Make the state visible beside the button that
  // resolves it, which is what the operator can actually act on.
  const profileDirty = profileFingerprint(profile) !== savedProfileRef.current;
  const prefsDirty = prefsFingerprint(prefs) !== savedPrefsRef.current;

  useEffect(() => {
    if (!dirty) return undefined;
    const onBeforeUnload = (e) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [dirty]);

  const savePrefs = async () => {
    // T267(3) — Number('') is 0, so clearing the digest-hour field did not fail
    // validation, it silently asked for midnight. Say what is wrong instead.
    const hour = Number(prefs.digestHour);
    if (prefs.digestHour === '' || !Number.isInteger(hour) || hour < 0 || hour > 23) {
      setError('Digest hour must be a whole number between 0 and 23.');
      toast('Digest hour must be a whole number between 0 and 23.', { kind: 'error' });
      return;
    }
    setPrefsBusy(true);
    setError(null);
    try {
      await api.patch('/preferences', prefsPayload(prefs));
      await refreshAuth();
      setPrefsSavedAt(new Date());
      // T267(5) — "saved" was the whole message, and preferences do nothing
      // until a scan runs. T220's wrong-location bug survived weeks partly
      // because the operator changed a setting, saw "saved", and reasonably
      // assumed the queue would reflect it.
      toast('Preferences saved — they apply on the next scan.', { kind: 'success' });
    } catch (e) {
      toast(e.body?.message || e.message, { kind: 'error' });
    } finally {
      setPrefsBusy(false);
    }
  };

  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      const payload = {
        ...profile,
        // Empty string seniority → omit (zod treats undefined as omitted)
        seniority: profile.seniority || undefined,
      };
      const body = await api.put('/profile', payload);
      const p = body.profile;
      const next = {
        summary: p.summary,
        targetNarrative: p.targetNarrative,
        seniority: p.seniority ?? '',
        skills: p.skills,
        experience: p.experience,
        education: p.education,
        certifications: p.certifications,
        languages: p.languages,
      };
      setProfile(next);
      savedProfileRef.current = profileFingerprint(next); // T163 — saved, no longer dirty
      setSavedAt(new Date());
      toast('Career profile saved', { kind: 'success' });
    } catch (e) {
      const msg = e.body?.message || e.message;
      toast(msg, { kind: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const loadImportCandidates = useCallback(async () => {
    setImportError(null);
    try {
      const body = await api.get('/profile/import-candidates');
      setImportCandidates({
        cv: body.cv ?? [],
        linkedinPdf: body.linkedinPdf ?? [],
        linkedinExport: body.linkedinExport ?? [],
      });
    } catch (e) {
      setImportError(e.body?.message || e.message);
    }
  }, []);

  const toggleImport = () => {
    setImportOpen((v) => {
      const next = !v;
      if (next) loadImportCandidates();
      return next;
    });
  };

  // Route each candidate type to the right endpoint:
  //   cv + linkedin-pdf → /import-cv (Claude, ~10s, tokens cost)
  //   linkedin-export   → /import-linkedin-zip (local CSV parse, instant, free)
  const runImport = async (docId, type) => {
    setImportBusyId(docId);
    setImportError(null);
    setImportDraft(null);
    try {
      const endpoint = type === 'linkedin-export' ? '/profile/import-linkedin-zip' : '/profile/import-cv';
      const body = await api.post(`${endpoint}?documentId=${encodeURIComponent(docId)}`);
      setImportDraft(body);
    } catch (e) {
      setImportError(e.body?.message || e.message);
    } finally {
      setImportBusyId(null);
    }
  };

  const applyImport = async () => {
    if (!importDraft) return;
    setSaving(true);
    setError(null);
    try {
      // Merge draft over the current profile — preserve user-edited fields
      // (targetNarrative, etc.) that the CV doesn't cover.
      const payload = {
        summary: importDraft.draft.summary || profile.summary,
        targetNarrative: profile.targetNarrative, // never overwritten by CV
        seniority: importDraft.draft.seniority || profile.seniority || undefined,
        skills: importDraft.draft.skills,
        experience: importDraft.draft.experience,
        education: importDraft.draft.education,
        certifications: importDraft.draft.certifications,
        languages: importDraft.draft.languages,
      };
      // T046 — tell the server which snapshot field to persist (cv vs
      // linkedin) so we don't lose the raw extraction when the operator
      // edits manually later. `documentType` is set by the import route.
      const snapshot =
        importDraft.documentType === 'cv'
          ? 'cv'
          : importDraft.documentType === 'linkedin-pdf' || importDraft.documentType === 'linkedin-export'
            ? 'linkedin'
            : null;
      const path = snapshot ? `/profile?snapshot=${snapshot}` : '/profile';
      const body = await api.put(path, payload);
      const p = body.profile;
      const next = {
        summary: p.summary,
        targetNarrative: p.targetNarrative,
        seniority: p.seniority ?? '',
        skills: p.skills,
        experience: p.experience,
        education: p.education,
        certifications: p.certifications,
        languages: p.languages,
      };
      setProfile(next);
      // T267(1) — this import WRITES the profile, exactly like save() does, but
      // never advanced the dirty baseline. So the page stayed permanently
      // "unsaved" after any import, and the beforeunload guard fired on a
      // profile with nothing left to lose — training the operator to click
      // through the browser's leave-warning, which is the one habit that makes
      // the guard useless when it is finally right.
      savedProfileRef.current = profileFingerprint(next);
      setSavedAt(new Date());
      setImportDraft(null);
      setImportOpen(false);
      toast(`Profile updated from ${importDraft.documentType === 'cv' ? 'CV' : 'LinkedIn'} import`, { kind: 'success' });
    } catch (e) {
      toast(e.body?.message || e.message, { kind: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const addExperience = () =>
    setProfile((p) => ({
      ...p,
      experience: [
        ...p.experience,
        { company: '', title: '', start: '', end: '', highlights: [], impact: [], stack: [] },
      ],
    }));

  const updateExp = (i, patch) =>
    setProfile((p) => ({
      ...p,
      experience: p.experience.map((e, idx) => (idx === i ? { ...e, ...patch } : e)),
    }));

  const removeExp = (i) =>
    setProfile((p) => ({ ...p, experience: p.experience.filter((_, idx) => idx !== i) }));

  // T072 — Education editor (parallel to experience). Shape per
  // shared/src/profile.js: { school, degree, field, startYear, endYear }.
  const addEducation = () =>
    setProfile((p) => ({
      ...p,
      education: [...p.education, { school: '', degree: '', field: '', startYear: '', endYear: '' }],
    }));
  const updateEdu = (i, patch) =>
    setProfile((p) => ({
      ...p,
      education: p.education.map((e, idx) => (idx === i ? { ...e, ...patch } : e)),
    }));
  const removeEdu = (i) =>
    setProfile((p) => ({ ...p, education: p.education.filter((_, idx) => idx !== i) }));

  if (loading) {
    return (
      <div className="space-y-8">
        <header className="border-b border-slate-800 pb-6">
          <h1 className="text-3xl font-semibold tracking-tight text-brand-500">Profile</h1>
          <p className="mt-1 text-sm text-slate-400">Loading…</p>
        </header>
        <FormSkeleton sections={4} />
      </div>
    );
  }

  // T044/T052-lite — Profile completeness signal. Each field gets a weight
  // proportional to how load-bearing it is for the daily scanner + LLM
  // explainer. The percent is shown in the header; below 70% the dashboard
  // recommendations are visibly worse, so we nudge.
  const completeness = computeCompleteness(profile, prefs);

  return (
    <div className="space-y-8">
      <header className="border-b border-slate-800 pb-6">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <h1 className="text-3xl font-semibold tracking-tight text-brand-500">Profile</h1>
            <p className="mt-1 text-sm text-slate-400">
              Career profile · the load-bearing entity for job matching
            </p>
          </div>
          <CompletenessGauge percent={completeness.percent} missing={completeness.missing} />
        </div>
        <div className="mt-3 flex flex-wrap gap-2 text-xs text-slate-500">
          <Badge ok={meta.hasCvSnapshot} label="CV imported" />
          <Badge ok={meta.hasLinkedinSnapshot} label="LinkedIn imported" />
          <Badge ok={meta.embeddingsReady} label="Embeddings ready" />
        </div>
      </header>

      {error && (
        <p className="rounded border border-red-800 bg-red-950/40 p-3 text-sm text-red-300">{error}</p>
      )}

      <section className="rounded-xl border border-slate-800 bg-slate-900/30 p-5">
        <button
          type="button"
          onClick={toggleImport}
          className="flex w-full items-start justify-between text-left"
        >
          <div>
            <h2 className="text-sm font-medium text-brand-500">Import from CV or LinkedIn</h2>
            <p className="mt-0.5 text-xs text-slate-500">
              Parse a stored PDF (Claude, ~10s) or a LinkedIn data export ZIP (local CSV parsing, instant).
            </p>
          </div>
          <span className="ml-3 text-xs text-slate-500">{importOpen ? '−' : '+'}</span>
        </button>

        {importOpen && (
          <div className="mt-4 space-y-4">
            {importError && (
              <p className="rounded border border-red-800 bg-red-950/40 p-2 text-xs text-red-300">
                {importError}
              </p>
            )}

            <ImportGroup
              title="CV (PDF)"
              hint="Parsed via Claude — requires an Anthropic key in Connectors."
              docs={importCandidates.cv}
              busyId={importBusyId}
              onParse={(id) => runImport(id, 'cv')}
              busyLabel="parsing… (~10s)"
            />
            <ImportGroup
              title="LinkedIn profile (PDF)"
              hint="Save your LinkedIn profile as PDF, upload it as type=linkedin-pdf. Same Claude parser."
              docs={importCandidates.linkedinPdf}
              busyId={importBusyId}
              onParse={(id) => runImport(id, 'linkedin-pdf')}
              busyLabel="parsing… (~10s)"
            />
            <ImportGroup
              title="LinkedIn data export (ZIP)"
              hint='Get yours from LinkedIn → Settings → Data Privacy → "Get a copy of your data". Upload as type=linkedin-export. Pure CSV parsing — no AI cost.'
              docs={importCandidates.linkedinExport}
              busyId={importBusyId}
              onParse={(id) => runImport(id, 'linkedin-export')}
              busyLabel="parsing…"
            />

            {importCandidates.cv.length === 0 &&
              importCandidates.linkedinPdf.length === 0 &&
              importCandidates.linkedinExport.length === 0 && (
                <p className="text-xs text-slate-500">
                  No importable documents yet.{' '}
                  <Link className="underline decoration-dotted hover:text-brand-500" to="/documents">
                    Upload one in Documents
                  </Link>{' '}
                  with type <code className="font-mono">cv</code>,{' '}
                  <code className="font-mono">linkedin-pdf</code>, or{' '}
                  <code className="font-mono">linkedin-export</code>.
                </p>
              )}
          </div>
        )}
      </section>

      {importDraft && (
        <ImportDiffPanel
          panelRef={importPanelRef}
          draft={importDraft}
          onApply={applyImport}
          onCancel={() => setImportDraft(null)}
          saving={saving}
        />
      )}

      {/* ─── Hunt preferences ───────────────────────────────────────────── */}
      <section className="rounded-xl border border-slate-800 bg-slate-900/30 p-5 space-y-5">
        <div>
          <h2 className="text-sm font-medium text-brand-500">What I'm looking for</h2>
          <p className="mt-0.5 text-xs text-slate-500">
            Drives the daily scanner — which roles, where, at what level. Empty means "no filter".
          </p>
        </div>

        <Section title="Target roles" hint="Press Enter or comma to add. Click × to remove.">
          <ChipInput
            value={prefs.targetRoles}
            onChange={(v) => setPrefs((p) => ({ ...p, targetRoles: v }))}
            placeholder="Cloud Engineer, Solution Architect, Application Developer"
            ariaLabel="Target roles"
          />
          {!!user?.connectors?.anthropicKey && (
            <AiSuggestPanel
              label="Suggest target roles"
              endpoint="/profile/ai/suggest-roles"
              itemKey="roles"
              nameKey="title"
              existing={prefs.targetRoles}
              onAdd={(title) =>
                setPrefs((p) => (p.targetRoles.includes(title) ? p : { ...p, targetRoles: [...p.targetRoles, title] }))
              }
              helperText="based on your CV"
            />
          )}
          <RoleCoverage savedAt={prefsSavedAt} />
        </Section>

        <Section title="Target locations" hint="Cities or countries. 'Copenhagen' resolves to DK.">
          <ChipInput
            value={prefs.targetLocations}
            onChange={(v) => setPrefs((p) => ({ ...p, targetLocations: v }))}
            placeholder="Copenhagen, Berlin, Stockholm, Remote Europe"
            ariaLabel="Target locations"
          />
          {!!user?.connectors?.anthropicKey && (
            <AiSuggestPanel
              label="Suggest target locations"
              endpoint="/profile/ai/suggest-locations"
              body={{ remotePreference: prefs.remotePreference }}
              itemKey="locations"
              nameKey="name"
              existing={prefs.targetLocations}
              onAdd={(name) =>
                setPrefs((p) => (p.targetLocations.includes(name) ? p : { ...p, targetLocations: [...p.targetLocations, name] }))
              }
              helperText="DK + Nordic + EU only"
            />
          )}
        </Section>

        <div className="grid gap-4 sm:grid-cols-2">
          <Section title="Target seniority" hint="Pick one or more levels. Empty = no preference.">
            {/* T096 — Multi-toggle: each level is an independent chip the
                operator turns on / off. The scorer rewards exact match
                against ANY of the selected levels (best wins). */}
            <div className="flex flex-wrap gap-1.5">
              {['junior', 'mid', 'senior', 'staff', 'principal'].map((s) => {
                const active = Array.isArray(prefs.seniority) && prefs.seniority.includes(s);
                return (
                  <button
                    key={s}
                    type="button"
                    onClick={() =>
                      setPrefs((p) => {
                        const cur = Array.isArray(p.seniority) ? p.seniority : [];
                        const next = cur.includes(s) ? cur.filter((x) => x !== s) : [...cur, s];
                        return { ...p, seniority: next };
                      })
                    }
                    className={
                      'rounded-full border px-3 py-1 text-xs transition ' +
                      (active
                        ? 'border-brand-500 bg-brand-600/20 text-brand-200'
                        : 'border-slate-700 text-slate-400 hover:border-brand-600 hover:text-brand-400')
                    }
                  >
                    {active ? '✓ ' : ''}{s}
                  </button>
                );
              })}
            </div>
          </Section>

          <Section title="Remote preference">
            <select
              value={prefs.remotePreference}
              onChange={(e) => setPrefs((p) => ({ ...p, remotePreference: e.target.value }))}
              className={inputCls}
            >
              {['any', 'remote', 'hybrid', 'onsite'].map((r) => (
                <option key={r} value={r}>{r}</option>
              ))}
            </select>
          </Section>

          <Section
            title="Salary minimum (annual)"
            hint="Leave the currency unset and the salary dimension is skipped rather than guessed at."
          >
            <div className="flex gap-2">
              <input
                type="number"
                min="0"
                step="1000"
                value={prefs.salaryMinimum}
                onChange={(e) => setPrefs((p) => ({ ...p, salaryMinimum: e.target.value }))}
                className={`${inputCls} flex-1`}
                placeholder="e.g. 750000"
              />
              {/* T314 — the floor used to be compared against whatever the ad
                  quoted. 1,165 of 1,177 "below floor" labels sat on jobs in
                  another currency; a 110,000 EUR role was called underpaid
                  against a 750,000 kroner floor. */}
              <select
                value={prefs.salaryCurrency}
                onChange={(e) => setPrefs((p) => ({ ...p, salaryCurrency: e.target.value }))}
                className={`${inputCls} w-28`}
                aria-label="Currency of the salary floor"
              >
                <option value="">not set</option>
                {['DKK', 'EUR', 'SEK', 'NOK', 'GBP', 'USD', 'CHF', 'PLN'].map((c) => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
            </div>
          </Section>

          <Section title="Daily digest hour" hint="0–23, in the scan timezone (Europe/Bucharest)">
            <input
              type="number"
              min="0"
              max="23"
              value={prefs.digestHour}
              onChange={(e) => setPrefs((p) => ({ ...p, digestHour: e.target.value }))}
              className={inputCls}
            />
          </Section>
        </div>

        <Section title="Excluded companies" hint="Hidden from the daily digest entirely.">
          <ChipInput
            value={prefs.excludedCompanies}
            onChange={(v) => setPrefs((p) => ({ ...p, excludedCompanies: v }))}
            placeholder="ScamCo, OldEmployer"
            ariaLabel="Excluded companies"
          />
        </Section>

        <div className="flex items-center gap-4">
          <button
            type="button"
            onClick={savePrefs}
            disabled={prefsBusy}
            className="rounded-md bg-brand-600 px-4 py-2 text-sm font-medium text-white transition hover:bg-brand-500 disabled:opacity-50"
          >
            {prefsBusy ? 'saving…' : 'Save preferences'}
          </button>
          {prefsDirty && !prefsBusy && (
            <span className="text-xs text-amber-300">● unsaved changes</span>
          )}
          {!prefsDirty && prefsSavedAt && !prefsBusy && (
            <span className="text-xs text-slate-500">saved {formatClock(prefsSavedAt)}</span>
          )}
        </div>
      </section>

      {/* T087 — Skill-gap snapshot. Compares your stated skills vs your
          last 30 recs. Cached server-side for 7 days. */}
      <SkillGapPanel aiKeyReady={!!user?.connectors?.anthropicKey} />

      {/* ─── Career profile (CV-style) ──────────────────────────────────── */}
      <section className="space-y-5">
        <h2 className="text-sm font-medium text-brand-500">Career profile</h2>
        <p className="-mt-3 text-xs text-slate-500">
          Who you are professionally. Used by the matcher when ranking job recommendations.
        </p>

      <Section title="Summary" hint="A 2-3 sentence elevator pitch.">
        <textarea
          rows={3}
          value={profile.summary}
          onChange={(e) => setProfile((p) => ({ ...p, summary: e.target.value }))}
          className={inputCls}
          placeholder="Cloud-native generalist with focus on delivery and stakeholder management…"
        />
        {!!user?.connectors?.anthropicKey && (
          <DraftSummaryButton
            onDraft={(text) => setProfile((p) => ({ ...p, summary: text }))}
          />
        )}
      </Section>

      <Section title="Target narrative" hint="What kind of roles? Why? Constraints?">
        <textarea
          rows={4}
          value={profile.targetNarrative}
          onChange={(e) => setProfile((p) => ({ ...p, targetNarrative: e.target.value }))}
          className={inputCls}
          placeholder="Senior cloud / solution architect roles in Copenhagen or Remote Europe…"
        />
      </Section>

      <Section title="Seniority">
        <select
          value={profile.seniority}
          onChange={(e) => setProfile((p) => ({ ...p, seniority: e.target.value }))}
          className={inputCls + ' max-w-xs'}
        >
          <option value="">— select —</option>
          {['junior', 'mid', 'senior', 'staff', 'principal'].map((s) => (
            <option key={s} value={s}>{s}</option>
          ))}
        </select>
      </Section>

      <Section title="Skills" hint="Press Enter or comma to add. Click × to remove.">
        <ChipInput
          value={profile.skills}
          onChange={(v) => setProfile((p) => ({ ...p, skills: v }))}
          placeholder="Oracle Cloud, Kubernetes, Node.js, Terraform"
          ariaLabel="Skills"
        />
        <p className="mt-1 text-xs text-slate-500">{profile.skills.length} skills</p>
      </Section>

      {/* T421 — the hint was "CV import (T008) will auto-populate", which
          names a ticket to the only person who will never read the board,
          and describes as future a feature that has been live for months. */}
      <Section
        title="Experience"
        hint={profile.experience.length === 0 ? 'Import a CV above to fill this in, or add entries by hand.' : null}
      >
        <ul className="space-y-3">
          {profile.experience.map((exp, i) => (
            <li key={i} className="rounded-lg border border-slate-800 bg-slate-900/40 p-4">
              <div className="grid gap-2 sm:grid-cols-2">
                <input
                  value={exp.company}
                  onChange={(e) => updateExp(i, { company: e.target.value })}
                  className={inputCls}
                  placeholder="Company"
                />
                <input
                  value={exp.title}
                  onChange={(e) => updateExp(i, { title: e.target.value })}
                  className={inputCls}
                  placeholder="Title"
                />
                <input
                  value={exp.start}
                  onChange={(e) => updateExp(i, { start: e.target.value })}
                  className={inputCls}
                  placeholder="Start (YYYY-MM)"
                />
                <input
                  value={exp.end}
                  onChange={(e) => updateExp(i, { end: e.target.value })}
                  className={inputCls}
                  placeholder="End (YYYY-MM, blank = current)"
                />
              </div>
              <textarea
                rows={3}
                value={(exp.highlights ?? []).join('\n')}
                onChange={(e) =>
                  updateExp(i, {
                    highlights: e.target.value.split('\n').map((s) => s.trim()).filter(Boolean),
                  })
                }
                className={inputCls + ' mt-2'}
                placeholder="One highlight per line"
              />
              <button
                type="button"
                onClick={() => removeExp(i)}
                className="mt-2 text-xs text-slate-500 underline decoration-dotted hover:text-red-400"
              >
                remove
              </button>
            </li>
          ))}
        </ul>
        <button
          type="button"
          onClick={addExperience}
          className="mt-3 rounded-md border border-slate-700 px-3 py-1.5 text-sm text-slate-300 hover:border-brand-500 hover:text-brand-500"
        >
          + add experience
        </button>
      </Section>

      {/* T072 — Education editor. Same pattern as Experience: card per
          entry, manual add, removable. Auto-populated from CV import
          when present. */}
      <Section
        title="Education"
        hint={profile.education.length === 0 ? 'CV / LinkedIn import auto-populates. Or add manually.' : null}
      >
        <ul className="space-y-3">
          {profile.education.map((edu, i) => (
            <li key={i} className="rounded-lg border border-slate-800 bg-slate-900/40 p-4">
              <div className="grid gap-2 sm:grid-cols-2">
                <input
                  value={edu.school ?? ''}
                  onChange={(e) => updateEdu(i, { school: e.target.value })}
                  className={inputCls}
                  placeholder="School / University"
                />
                <input
                  value={edu.degree ?? ''}
                  onChange={(e) => updateEdu(i, { degree: e.target.value })}
                  className={inputCls}
                  placeholder="Degree (e.g. BSc, MSc, PhD)"
                />
                <input
                  value={edu.field ?? ''}
                  onChange={(e) => updateEdu(i, { field: e.target.value })}
                  className={inputCls + ' sm:col-span-2'}
                  placeholder="Field of study (e.g. Computer Science)"
                />
                <input
                  value={edu.startYear ?? ''}
                  onChange={(e) => updateEdu(i, { startYear: e.target.value })}
                  className={inputCls}
                  placeholder="Start year (e.g. 2010)"
                  inputMode="numeric"
                />
                <input
                  value={edu.endYear ?? ''}
                  onChange={(e) => updateEdu(i, { endYear: e.target.value })}
                  className={inputCls}
                  placeholder="End year (blank = ongoing)"
                  inputMode="numeric"
                />
              </div>
              <button
                type="button"
                onClick={() => removeEdu(i)}
                className="mt-2 text-xs text-slate-500 underline decoration-dotted hover:text-red-400"
              >
                remove
              </button>
            </li>
          ))}
        </ul>
        <button
          type="button"
          onClick={addEducation}
          className="mt-3 rounded-md border border-slate-700 px-3 py-1.5 text-sm text-slate-300 hover:border-brand-500 hover:text-brand-500"
        >
          + add education
        </button>
      </Section>

      <Section title="Languages" hint="Defaults to professional level. Edit individual rows later for native/fluent.">
        <ChipInput
          value={profile.languages.map((l) => l.name)}
          onChange={(names) =>
            setProfile((p) => ({
              ...p,
              languages: remapByName(names, p.languages, { level: 'professional' }),
            }))
          }
          placeholder="English, Romanian, French"
          ariaLabel="Languages"
        />
      </Section>

      <Section title="Certifications" hint="Names only — issuer/year edit lands later.">
        <ChipInput
          value={profile.certifications.map((c) => c.name)}
          onChange={(names) =>
            setProfile((p) => ({
              ...p,
              certifications: remapByName(names, p.certifications, { issuer: '', url: '' }),
            }))
          }
          placeholder="CKA, OCI Architect Professional, AWS SAA"
          ariaLabel="Certifications"
        />
      </Section>

        <div className="flex items-center gap-4">
          <button
            type="button"
            onClick={save}
            disabled={saving || !!importDraft}
            title={
              importDraft
                ? 'A parsed draft is waiting above — click "Use this profile" inside that panel first.'
                : undefined
            }
            className="rounded-md bg-brand-600 px-4 py-2 text-sm font-medium text-white transition hover:bg-brand-500 disabled:opacity-50"
          >
            {saving ? 'saving…' : 'Save career profile'}
          </button>
          {importDraft && (
            <span className="text-xs text-amber-300">
              ⓘ Apply or discard the pending draft above before saving manual edits.
            </span>
          )}
          {profileDirty && !saving && !importDraft && (
            <span className="text-xs text-amber-300">● unsaved changes</span>
          )}
          {!profileDirty && savedAt && !saving && !importDraft && (
            <span className="text-xs text-slate-500">saved {formatClock(savedAt)}</span>
          )}
        </div>
      </section>
    </div>
  );
}

// T086 — Draft summary button. Calls /profile/ai/draft-summary, lets the
// operator preview the result before adopting it (replaces the textarea
// content if they click Use). Rationale shown so they know why.
function DraftSummaryButton({ onDraft }) {
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(null);
  const [error, setError] = useState('');

  const draft = async () => {
    setBusy(true);
    setError('');
    try {
      const r = await api.post('/profile/ai/draft-summary', {});
      setPreview(r);
    } catch (e) {
      setError(e?.body?.message || e?.message || 'Draft failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mt-2">
      <button
        type="button"
        onClick={preview ? () => setPreview(null) : draft}
        disabled={busy}
        className="rounded-md border border-slate-700 px-2.5 py-1 text-[11px] text-slate-300 hover:border-brand-600 hover:text-brand-400 disabled:opacity-50"
      >
        {busy ? '✨ Drafting…' : preview ? '× discard preview' : '✨ Draft summary'}
      </button>
      {error && <p className="mt-1 text-xs text-red-400">⚠ {error}</p>}
      {preview && (
        <div className="mt-2 rounded-lg border border-brand-700 bg-brand-600/5 p-3">
          <p className="text-[10px] uppercase tracking-widest text-brand-400">Suggested summary</p>
          <p className="mt-1 text-sm leading-snug text-slate-100">{preview.summary}</p>
          {preview.rationale && (
            <p className="mt-1 text-[11px] italic text-slate-500">{preview.rationale}</p>
          )}
          <div className="mt-2 flex gap-2">
            <button
              type="button"
              onClick={() => {
                onDraft?.(preview.summary);
                setPreview(null);
                toast('Summary applied — review + save preferences', { kind: 'success' });
              }}
              className="rounded-md bg-brand-600 px-3 py-1 text-xs font-medium text-white hover:bg-brand-500"
            >
              Use this →
            </button>
            <button
              type="button"
              onClick={() => setPreview(null)}
              className="rounded-md border border-slate-700 px-3 py-1 text-xs text-slate-300 hover:border-brand-600 hover:text-brand-400"
            >
              Discard
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function Section({ title, hint, children }) {
  return (
    <section>
      <h2 className="text-xs uppercase tracking-wide text-slate-500">{title}</h2>
      {hint && <p className="mt-0.5 text-xs text-slate-500">{hint}</p>}
      <div className="mt-2">{children}</div>
    </section>
  );
}

function Badge({ ok, label }) {
  return (
    <span
      className={
        'rounded px-2 py-0.5 ' +
        (ok ? 'bg-brand-600/20 text-brand-500' : 'bg-slate-800 text-slate-500')
      }
    >
      {ok ? '✓' : '·'} {label}
    </span>
  );
}

// Profile-completeness scoring. Weights chosen by *how much* each field
// shifts the matching pipeline: target roles + skills + locations matter
// most for the daily scanner; summary + experience matter most for the
// LLM explainer. Returns {percent, missing: [label]}.
const COMPLETENESS_FIELDS = [
  { weight: 18, key: 'targetRoles', label: 'target roles', test: (p, pr) => pr.targetRoles.length > 0 },
  { weight: 14, key: 'targetLocations', label: 'target locations', test: (p, pr) => pr.targetLocations.length > 0 },
  { weight: 6, key: 'seniorityPref', label: 'target seniority', test: (p, pr) => Array.isArray(pr.seniority) ? pr.seniority.length > 0 : !!pr.seniority },
  { weight: 18, key: 'skills', label: 'skills', test: (p) => p.skills.length >= 3 },
  { weight: 16, key: 'experience', label: 'experience', test: (p) => p.experience.length > 0 },
  { weight: 12, key: 'summary', label: 'summary', test: (p) => (p.summary ?? '').length > 30 },
  { weight: 8, key: 'targetNarrative', label: 'target narrative', test: (p) => (p.targetNarrative ?? '').length > 30 },
  { weight: 4, key: 'languages', label: 'languages', test: (p) => p.languages.length > 0 },
  { weight: 4, key: 'certifications', label: 'certifications', test: (p) => p.certifications.length > 0 },
  { weight: 4, key: 'education', label: 'education', test: (p) => (p.education ?? []).length > 0 },
];

function computeCompleteness(profile, prefs) {
  let achieved = 0;
  let total = 0;
  const missing = [];
  for (const f of COMPLETENESS_FIELDS) {
    total += f.weight;
    if (f.test(profile, prefs)) achieved += f.weight;
    else missing.push(f.label);
  }
  return { percent: Math.round((achieved / total) * 100), missing };
}

function CompletenessGauge({ percent, missing }) {
  // Three-tier colouring: red <40, amber <70, brand-accent >= 70.
  const tier =
    percent >= 70 ? 'text-emerald-400 border-emerald-700 bg-emerald-950/30'
    : percent >= 40 ? 'text-amber-300 border-amber-700 bg-amber-950/30'
    : 'text-red-400 border-red-800 bg-red-950/30';
  return (
    <div className={`rounded-xl border px-4 py-3 text-right ${tier}`} title={missing.length > 0 ? `Missing: ${missing.join(', ')}` : 'Profile is complete'}>
      <div className="text-3xl font-semibold leading-none tracking-tight">{percent}<span className="text-base">%</span></div>
      <div className="mt-0.5 text-[10px] uppercase tracking-wide opacity-75">complete</div>
      {missing.length > 0 && (
        <div className="mt-1 max-w-[200px] text-xs opacity-80">
          missing: <span className="font-medium">{missing.slice(0, 3).join(', ')}{missing.length > 3 ? '…' : ''}</span>
        </div>
      )}
    </div>
  );
}

function ImportGroup({ title, hint, docs, busyId, onParse, busyLabel }) {
  if (docs.length === 0) {
    return (
      <div className="rounded border border-slate-800/60 bg-slate-900/20 p-3">
        <div className="text-xs font-medium text-slate-400">{title}</div>
        <p className="mt-1 text-xs text-slate-500">{hint}</p>
        <p className="mt-1 text-xs text-slate-500">— no documents of this type yet</p>
      </div>
    );
  }
  return (
    <div className="rounded border border-slate-800/60 bg-slate-900/20 p-3">
      <div className="text-xs font-medium text-slate-300">{title}</div>
      <p className="mt-0.5 text-xs text-slate-500">{hint}</p>
      <ul className="mt-2 space-y-2">
        {docs.map((d) => (
          <li
            key={d.id}
            className="flex items-center justify-between rounded border border-slate-800 bg-slate-900/40 p-2 text-sm"
          >
            <div>
              <div className="text-slate-200">{d.originalFilename}</div>
              <div className="text-xs text-slate-500">
                v{d.version} · {(d.sizeBytes / 1024).toFixed(1)} KB ·{' '}
                {formatDate(d.createdAt)}
              </div>
            </div>
            <button
              type="button"
              onClick={() => onParse(d.id)}
              disabled={!!busyId}
              className="rounded-md border border-brand-600 px-3 py-1 text-xs text-brand-500 transition hover:bg-brand-600/10 disabled:opacity-50"
            >
              {busyId === d.id ? busyLabel : 'parse'}
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}

function ImportDiffPanel({ panelRef, draft, onApply, onCancel, saving }) {
  const { draft: d, changes, source } = draft;
  const isZip = source.csvFilesFound !== undefined;
  return (
    <section
      ref={panelRef}
      className="rounded-xl border-2 border-brand-600 bg-brand-600/10 p-5 shadow-lg shadow-brand-600/10"
    >
      <header className="flex items-start justify-between gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-wide text-amber-400">
            Step 2 of 2 — review + apply
          </div>
          <h2 className="mt-0.5 text-base font-semibold text-brand-500">
            Draft profile parsed from {isZip ? 'LinkedIn export' : 'CV'}
          </h2>
          <p className="mt-0.5 text-xs text-slate-500">
            Source: <code className="font-mono">{source.filename}</code> ·{' '}
            {isZip
              ? `${source.csvFilesFound.length} CSV(s): ${source.csvFilesFound.join(', ')}`
              : `${source.model} · ${source.tokensIn}→${source.tokensOut} tokens`}
            . Targets won't be overwritten.
          </p>
        </div>
        <button
          type="button"
          onClick={onCancel}
          className="text-xs text-slate-500 underline decoration-dotted hover:text-slate-300"
        >
          discard
        </button>
      </header>

      <div className="mt-4 grid gap-3 sm:grid-cols-2">
        <div className="rounded border border-slate-800 bg-slate-900/40 p-3">
          <div className="text-xs uppercase tracking-wide text-slate-500">Changes</div>
          {changes.length === 0 ? (
            <p className="mt-2 text-xs text-slate-500">No differences from current profile.</p>
          ) : (
            <ul className="mt-2 space-y-1 text-xs">
              {changes.map((c, i) => (
                <li key={i} className="flex justify-between gap-2">
                  <span className="text-slate-300">
                    <span className="font-mono text-brand-500">{c.action}</span> · {c.field}
                  </span>
                  <span className="text-slate-400">
                    {typeof c.after === 'string'
                      ? `"${(c.after || '').slice(0, 30)}${(c.after || '').length > 30 ? '…' : ''}"`
                      : `${c.before?.count ?? 0} → ${c.after?.count ?? 0}`}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="rounded border border-slate-800 bg-slate-900/40 p-3 text-xs">
          <div className="uppercase tracking-wide text-slate-500">Draft summary</div>
          <dl className="mt-2 space-y-1 text-slate-400">
            <DLine k="summary" v={d.summary ? `${d.summary.slice(0, 60)}…` : '—'} />
            <DLine k="seniority" v={d.seniority ?? '—'} />
            <DLine k="skills" v={`${d.skills.length} items`} />
            <DLine k="experience" v={`${d.experience.length} roles`} />
            <DLine k="education" v={`${d.education.length} entries`} />
            <DLine k="certifications" v={`${d.certifications.length} entries`} />
            <DLine k="languages" v={`${d.languages.length} entries`} />
          </dl>
        </div>
      </div>

      <div className="mt-4 flex flex-wrap items-center gap-3">
        <button
          type="button"
          onClick={onApply}
          disabled={saving}
          className="rounded-md bg-brand-600 px-5 py-2.5 text-sm font-semibold text-white shadow-md shadow-brand-600/30 transition hover:bg-brand-500 disabled:opacity-50"
        >
          {saving ? 'saving…' : '✓ Use this profile'}
        </button>
        <button
          type="button"
          onClick={onCancel}
          disabled={saving}
          className="rounded-md border border-slate-700 px-4 py-2 text-sm text-slate-300 hover:border-slate-500 disabled:opacity-50"
        >
          Discard
        </button>
        <p className="text-xs text-slate-500">
          The "Save career profile" button below stays disabled until you apply or discard this draft.
        </p>
      </div>
    </section>
  );
}

function DLine({ k, v }) {
  return (
    <div className="flex justify-between gap-2">
      <span className="text-slate-500">{k}</span>
      <span className="text-slate-300">{v}</span>
    </div>
  );
}
